import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Icon, type IconName } from '../components/ui/Icon'
import { Breadcrumbs } from '../components/ui/Breadcrumbs'
import { TechnologyFamilyField } from './TechnologyFamilyField'
import { normaliseUsageScopeState, usageScopeIsComplete } from './usage-scope-state'
import { createSerialSaveQueue, isUsageScopeValidationError } from './draft-save-coordinator'
import { useTenant } from '../tenancy/TenantProvider'
import { ErrorAlert, PageHeader } from '../portfolio/PortfolioCommon'
import {
  createPrivateProductForDraft,
  abandonAiUseDraft,
  finaliseAiUseDraft,
  getAiUseDraft,
  getAiUseEntryOptions,
  listOrganisationAiSolutions,
  previewAiUseScreening,
  saveAiUseDraft,
  searchProductLibrary,
  type AiUseDraft,
  type EntryOptions,
  type OrganisationSolution,
  type ProductLibraryResult,
  type ScreeningPreview,
} from './register-api'
import { DiscardDraftDialog } from './DiscardDraftDialog'
import {
  contributorStateFromOperational,
  deriveClassifications,
  deriveScreening,
  operationalStateFromContributor,
  shouldAskActionAuthority,
  shouldAskAffectedPeople,
  shouldAskContestability,
  shouldAskVulnerablePeople,
  type ActivityObservation,
  type AnalysisPurposeObservation,
  type ContributorObservations,
  type HumanCheckObservation,
  type MediaObservation,
  type TriStateAnswer,
} from './ai-use-disclosure-model'

type DraftData = Record<string, unknown>
type ProductChoice = 'known' | 'another' | 'internal' | 'unknown'
type DraftSaveInput = { nextStep: number; data: DraftData; fields: string[]; revision: number }
type DraftSavePhase = 'idle' | 'dirty' | 'saving' | 'saved' | 'failed'
type DraftSaveState = { phase: DraftSavePhase; fingerprint?: string; error?: unknown; savedAt?: string }
type FinaliseIssue = { error: unknown; kind: 'usage_validation' | 'operation'; usageRevision: string }

const steps = [
  'Tell us about the use',
  'How do you use it?',
  'Why are you using it?',
  'Does anything need extra attention?',
  'Review and add',
]

const lifecycleChoices = [
  { value: 'considering', title: 'Considering it', description: 'We are thinking about using it.' },
  { value: 'testing', title: 'Testing it', description: 'We are trying it before wider use.' },
  { value: 'in_use', title: 'In use', description: 'People are using it now.' },
  { value: 'no_longer_used', title: 'No longer used', description: 'It was used previously.' },
] as const

const activityChoices: { value: ActivityObservation; icon: IconName; title: string; description: string }[] = [
  { value: 'write_create', icon: 'pen', title: 'Write or create something', description: 'Emails, documents, images, presentations, code or other content.' },
  { value: 'find_information', icon: 'search', title: 'Find information or answer questions', description: 'Searches for information or helps answer a question.' },
  { value: 'read_documents', icon: 'document', title: 'Read or understand documents', description: 'Summarises, extracts, compares or organises information from documents.' },
  { value: 'analyse_predict_recommend', icon: 'chart', title: 'Analyse, predict or recommend', description: 'Analyses information, forecasts an outcome, scores something or suggests what someone should do.' },
  { value: 'process_actions', icon: 'workflow', title: 'Carry out parts of a process', description: 'Creates records, sends something, updates another system or performs steps automatically.' },
  { value: 'media_understanding', icon: 'media', title: 'Understand images, video or sound', description: 'Interprets images, video, speech or audio.' },
  { value: 'control_physical', icon: 'administration', title: 'Control equipment or machinery', description: 'Influences a robot, vehicle, industrial system or other physical equipment.' },
  { value: 'other_unsure', icon: 'help', title: "Something else / I'm not sure", description: 'Choose this if none of the descriptions fit or you are unsure.' },
]

const analysisPurposeChoices: { value: AnalysisPurposeObservation; title: string; description: string }[] = [
  { value: 'predict_forecast', title: 'Predict what may happen', description: 'Forecast demand, failure, behaviour, cost or another future outcome.' },
  { value: 'classify_score', title: 'Group, rank or score something', description: 'Categorise information, prioritise work or calculate a score.' },
  { value: 'recommend_support_decision', title: 'Recommend an option for a person', description: 'A person remains responsible for deciding what happens.' },
  { value: 'select_make_decision', title: 'Select or materially decide the outcome', description: 'The AI selects an outcome or determines which option proceeds.' },
  { value: 'detect_monitor', title: 'Detect or monitor something', description: 'Identify patterns, events, faults, anomalies or changing conditions.' },
  { value: 'personalise_experience', title: 'Personalise an experience', description: 'Change content or a service for a person, context or behaviour.' },
  { value: 'optimise_plan', title: 'Plan or optimise', description: 'Improve a schedule, allocation, route or other plan.' },
  { value: 'unsure', title: "I'm not sure", description: 'Keep the technical classification visible for confirmation later.' },
]

const userChoices = [
  { value: 'employees', title: 'Employees', icon: 'work' as IconName },
  { value: 'contractors', title: 'Contractors', icon: 'user' as IconName },
  { value: 'customers_clients', title: 'Customers or clients', icon: 'users' as IconName },
  { value: 'applicants', title: 'Applicants', icon: 'search' as IconName },
  { value: 'students', title: 'Students', icon: 'document' as IconName },
  { value: 'patients_care_recipients', title: 'Patients or care recipients', icon: 'heart' as IconName },
  { value: 'members_public', title: 'Members of the public', icon: 'users' as IconName },
  { value: 'other', title: 'Other', icon: 'plus' as IconName },
  { value: 'needs_confirmation', title: "I'm not sure", icon: 'help' as IconName },
]

const affectedChoices = [
  { value: 'employees_contractors', title: 'Employees or contractors' },
  { value: 'customers_clients', title: 'Customers or clients' },
  { value: 'applicants', title: 'Applicants' },
  { value: 'students', title: 'Students' },
  { value: 'patients_care_recipients', title: 'Patients or care recipients' },
  { value: 'members_public', title: 'Members of the public' },
  { value: 'no_identifiable_people', title: 'No identifiable people' },
  { value: 'other', title: 'Other' },
  { value: 'needs_confirmation', title: "I'm not sure" },
]

const benefitChoices: { value: string; icon: IconName; title: string; description: string }[] = [
  { value: 'time_workforce_capacity', icon: 'clock', title: 'Save time or reduce manual work', description: 'Reduce repetitive work or help people complete work faster.' },
  { value: 'reduced_cost', icon: 'dollar', title: 'Reduce cost', description: 'Reduce the cost of delivering an activity or service.' },
  { value: 'better_quality_consistency', icon: 'check', title: 'Improve quality or consistency', description: 'Improve accuracy, consistency or quality.' },
  { value: 'better_customer_service_user_experience', icon: 'heart', title: 'Improve customer or service-user experience', description: 'Make a service easier, faster or better for the people using it.' },
  { value: 'revenue_growth', icon: 'trend', title: 'Increase revenue or growth', description: 'Help generate revenue, demand or new opportunities.' },
  { value: 'reduced_risk_resilience', icon: 'shield', title: 'Reduce risk or improve resilience', description: 'Reduce errors, disruption, risk or operational exposure.' },
  { value: 'better_employee_experience', icon: 'users', title: 'Improve employee experience', description: 'Make work easier or remove frustrating manual tasks.' },
  { value: 'accessibility_inclusion', icon: 'accessibility', title: 'Improve accessibility or inclusion', description: 'Make information or services easier for more people to use.' },
  { value: 'learning_innovation', icon: 'lightbulb', title: 'Support learning or innovation', description: 'Help the organisation learn, experiment or develop something new.' },
  { value: 'other', icon: 'plus', title: 'Other', description: 'Another improvement that is important for this use.' },
]

const humanCheckChoices: { value: HumanCheckObservation; title: string; description: string }[] = [
  { value: 'every', title: 'Someone checks every output', description: 'A person reviews it before it is used or acted on.' },
  { value: 'some', title: 'Someone checks some outputs', description: 'Routine outputs may proceed, but some are reviewed.' },
  { value: 'none', title: 'It is normally used without being checked', description: 'There is no routine human review before use.' },
  { value: 'unsure', title: "I'm not sure", description: 'Someone else can confirm the review arrangement later.' },
]

const lifecycleLabels: Record<string, string> = Object.fromEntries(lifecycleChoices.map((choice) => [choice.value, choice.title]))
const userLabels: Record<string, string> = Object.fromEntries(userChoices.map((choice) => [choice.value, choice.title]))
const benefitLabels: Record<string, string> = Object.fromEntries(benefitChoices.map((choice) => [choice.value, choice.title]))
const humanCheckLabels: Record<string, string> = Object.fromEntries(humanCheckChoices.map((choice) => [choice.value, choice.title]))

function asString(value: unknown) { return typeof value === 'string' ? value : '' }
function asStrings(value: unknown) { return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [] }
function asObject(value: unknown): Record<string, unknown> { return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {} }
function displayDateTime(value: string) { return new Date(value).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) }

function toggle(values: string[], value: string, exclusiveValues: string[] = []) {
  if (values.includes(value)) return values.filter((item) => item !== value)
  if (exclusiveValues.includes(value)) return [value]
  return [...values.filter((item) => !exclusiveValues.includes(item)), value]
}

function triFromScreening(value: unknown): TriStateAnswer | undefined {
  if (value === 'yes' || value === 'no') return value
  if (value === 'needs_confirmation') return 'unsure'
  return undefined
}

function legacyObservations(data: DraftData): ContributorObservations {
  const existing = asObject(data.contributorObservations)
  if (Object.keys(existing).length > 0) {
    return {
      activities: asStrings(existing.activities) as ActivityObservation[],
      analysisPurpose: asString(existing.analysisPurpose) as AnalysisPurposeObservation || undefined,
      mediaType: asString(existing.mediaType) as MediaObservation || undefined,
      actionWithoutApproval: asString(existing.actionWithoutApproval) as TriStateAnswer || undefined,
      humanCheck: asString(existing.humanCheck) as HumanCheckObservation || undefined,
      userGroups: asStrings(existing.userGroups),
      affectedPeople: asStrings(existing.affectedPeople),
      informationHandling: asString(existing.informationHandling) as TriStateAnswer || undefined,
      importantDecision: asString(existing.importantDecision) as TriStateAnswer || undefined,
      seriousImpact: asString(existing.seriousImpact) as TriStateAnswer || undefined,
      regulatedArea: asString(existing.regulatedArea) as TriStateAnswer || undefined,
      vulnerablePeople: asString(existing.vulnerablePeople) as TriStateAnswer || undefined,
      contestability: asString(existing.contestability) as TriStateAnswer || undefined,
      otherActivityText: asString(existing.otherActivityText),
    }
  }

  const classifications = asObject(data.classifications)
  const canonicalActivities = asStrings(classifications.ai_activity)
  const activities: ActivityObservation[] = []
  if (canonicalActivities.includes('create_transform_content')) activities.push('write_create')
  if (canonicalActivities.includes('find_answer_information')) activities.push('find_information')
  if (canonicalActivities.includes('read_process_documents')) activities.push('read_documents')
  if (canonicalActivities.some((code) => ['predict_forecast', 'classify_score', 'recommend_support_decision', 'select_make_decision', 'detect_monitor', 'personalise_experience', 'optimise_plan'].includes(code))) activities.push('analyse_predict_recommend')
  if (canonicalActivities.includes('automate_process')) activities.push('process_actions')
  if (canonicalActivities.some((code) => ['analyse_images_video', 'understand_generate_speech'].includes(code))) activities.push('media_understanding')
  if (canonicalActivities.includes('control_physical_system')) activities.push('control_physical')
  if (canonicalActivities.includes('other_needs_confirmation')) activities.push('other_unsure')
  const analysisPurpose = canonicalActivities.find((code): code is AnalysisPurposeObservation => [
    'predict_forecast', 'classify_score', 'recommend_support_decision', 'select_make_decision',
    'detect_monitor', 'personalise_experience', 'optimise_plan',
  ].includes(code))

  const oversight = asString(classifications.human_oversight)
  const humanCheck: HumanCheckObservation | undefined = oversight === 'review_every_output_action'
    ? 'every'
    : oversight === 'review_selected_exceptional' || oversight === 'review_after_output_action'
      ? 'some'
      : oversight === 'no_routine_review'
        ? 'none'
        : oversight === 'needs_confirmation'
          ? 'unsure'
          : undefined

  const authority = asString(classifications.decision_authority)
  const actionWithoutApproval: TriStateAnswer | undefined = authority === 'bounded_actions_without_prior_approval'
    ? 'yes'
    : authority === 'action_after_person_approves' || authority === 'information_content_only' || authority === 'recommend_action_decision'
      ? 'no'
      : authority === 'needs_confirmation'
        ? 'unsure'
        : undefined

  const screening = asObject(data.screeningAnswers)
  return {
    activities,
    analysisPurpose,
    actionWithoutApproval,
    humanCheck,
    userGroups: asStrings(classifications.user_group),
    affectedPeople: asStrings(classifications.affected_people),
    informationHandling: triFromScreening(screening['GAS-01']),
    regulatedArea: triFromScreening(screening['GAS-05']),
    vulnerablePeople: triFromScreening(screening['GAS-04']),
    contestability: triFromScreening(screening['GAS-06']),
  }
}

function generalPurposeFromKnownData(draft: AiUseDraft, data: DraftData): 'yes' | 'no' | 'needs_confirmation' {
  const explicit = data.productGeneralPurposeAnswer
  if (explicit === 'yes' || explicit === 'no') return explicit
  const content = asObject(draft.catalogueSnapshot?.content)
  const known = content.generalPurpose ?? content.isGeneralPurpose
  if (known === true || known === 'yes') return 'yes'
  if (known === false || known === 'no') return 'no'
  return 'needs_confirmation'
}

function prepareDraftData(draft: AiUseDraft, data: DraftData, observations: ContributorObservations, activeBusinessUnitIds?: readonly string[]) {
  const existingClassifications = asObject(data.classifications)
  const existingTechnology = asStrings(existingClassifications.technology_family).filter((code) => code !== 'needs_technical_confirmation')
  const expectedBenefits = asStrings(data.expectedBenefits).length > 0 ? asStrings(data.expectedBenefits) : asStrings(existingClassifications.expected_benefit)
  const derivedClassifications = deriveClassifications(observations, expectedBenefits, existingTechnology)
  const screening = deriveScreening(observations, generalPurposeFromKnownData(draft, data))
  const contributorLifecycle = asString(data.contributorLifecycle) || contributorStateFromOperational(asString(data.operationalState) || 'proposed')
  const usageState = normaliseUsageScopeState(data, activeBusinessUnitIds)

  return {
    ...data,
    usageScope: usageState.scope,
    usageBusinessUnitIds: usageState.businessUnitIds,
    contributorLifecycle,
    operationalState: operationalStateFromContributor(contributorLifecycle),
    expectedBenefits,
    contributorObservations: observations,
    classifications: {
      ...existingClassifications,
      ...derivedClassifications,
      organisational_capability: asStrings(existingClassifications.organisational_capability),
    },
    screeningAnswers: screening.answers,
    screeningProvenance: screening.provenance,
    derivedMappings: {
      version: 'r1-contributor-disclosure-v1',
      activity: 'Contributor activity observations → controlled AI activity classifications',
      actionAuthority: 'Observable system-action and decision-impact answers → decision/action authority',
      audience: 'Direct user groups → audience scope',
      technologyFamily: asString(data.technologyFamilySource) === 'product_library'
        ? 'Inherited from the Product Library'
        : existingTechnology.length > 0
          ? 'Selected for this new or internally built AI'
          : 'Needs technical confirmation',
      screening: 'Contributor observations + deterministic relevance + Product/Solution confirmation state → GAS-01…GAS-07',
    },
  }
}

function IconChoice({
  type,
  name,
  value,
  checked,
  icon,
  title,
  description,
  onChange,
}: {
  type: 'checkbox' | 'radio'
  name: string
  value: string
  checked: boolean
  icon?: IconName
  title: string
  description?: string
  onChange: () => void
}) {
  return (
    <label className={`disclosure-choice-card${icon ? ' has-icon' : ''}${checked ? ' selected' : ''}`}>
      <input type={type} name={name} value={value} checked={checked} onChange={onChange} />
      {icon ? <span className="disclosure-choice-icon"><Icon name={icon} size={20} /></span> : null}
      <span className="disclosure-choice-copy"><strong>{title}</strong>{description ? <small>{description}</small> : null}</span>
    </label>
  )
}

function TriQuestion({
  name,
  icon,
  title,
  help,
  value,
  onChange,
}: {
  name: string
  icon: IconName
  title: string
  help: string
  value?: TriStateAnswer
  onChange: (value: TriStateAnswer) => void
}) {
  return (
    <fieldset className="disclosure-question-card progressive-reveal">
      <legend><span className="disclosure-question-icon"><Icon name={icon} size={20} /></span><span>{title}</span></legend>
      <p>{help}</p>
      <div className="disclosure-tri-options">
        {([['yes', 'Yes'], ['no', 'No'], ['unsure', "I'm not sure"]] as const).map(([option, label]) => (
          <label key={option} className={value === option ? 'selected' : ''}>
            <input type="radio" name={name} checked={value === option} onChange={() => onChange(option)} />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function ProductPicker({
  organisationId,
  draft,
  data,
  setData,
  solutions,
  onCreatePrivateProduct,
  creatingPrivateProduct,
}: {
  organisationId: string
  draft: AiUseDraft
  data: DraftData
  setData: (patch: DraftData) => void
  solutions: OrganisationSolution[]
  onCreatePrivateProduct: (input: { supplierName: string; productName: string; description: string; mode: 'another' | 'internal' }) => void
  creatingPrivateProduct: boolean
}) {
  const [query, setQuery] = useState('')
  const productChoice = (asString(data.productChoice) || (asString(data.productId) || asString(data.privateProductId) || asString(data.solutionId) ? 'known' : 'unknown')) as ProductChoice
  const resultsQuery = useQuery({
    queryKey: ['disclosure-product-search', organisationId, query],
    queryFn: () => searchProductLibrary(organisationId, query),
    enabled: Boolean(organisationId && query.trim().length >= 2),
  })

  const results = useMemo(() => {
    const seen = new Set<string>()
    return (resultsQuery.data ?? []).filter((result) => {
      const key = result.productId ?? `${result.supplierName ?? ''}:${result.productName ?? ''}`
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    }).slice(0, 8)
  }, [resultsQuery.data])

  const matchingSolutions = query.trim().length >= 2
    ? solutions.filter((solution) => `${solution.name} ${solution.productName ?? ''} ${solution.supplierName ?? ''}`.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 5)
    : []

  const selectedName = asString(data.productDisplayName)
    || solutions.find((solution) => solution.id === asString(data.solutionId))?.productName
    || draft.catalogueSnapshot?.products[0]?.name
    || ''
  const selectedSupplier = asString(data.productSupplierName)
    || solutions.find((solution) => solution.id === asString(data.solutionId))?.supplierName
    || draft.catalogueSnapshot?.products[0]?.supplierName
    || ''

  const clearProduct = () => {
    const classifications = asObject(data.classifications)
    setData({
      productId: '', offeringId: '', privateProductId: '', solutionId: '', solutionName: '', productDisplayName: '', productSupplierName: '',
      classifications: { ...classifications, technology_family: [] },
      technologyFamilySource: '',
    })
  }

  return (
    <fieldset className="disclosure-product-picker">
      <legend>What AI product or tool do you use?</legend>
      <p className="field-help">Choose it if you recognise it. It is okay if you do not know the exact product.</p>

      {productChoice === 'known' && selectedName ? (
        <div className="disclosure-selected-product">
          <span className="disclosure-choice-icon"><Icon name="check" size={20} /></span>
          <span><strong>{selectedName}</strong><small>{selectedSupplier || 'Known organisational product or solution'}</small></span>
          <button type="button" className="text-link" onClick={() => { clearProduct(); setData({ productChoice: 'unknown' }) }}>Change</button>
        </div>
      ) : (
        <>
          <label className="disclosure-product-search"><span>Search products and tools</span><div><Icon name="search" size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="e.g. ChatGPT, Microsoft Copilot" /></div></label>
          {query.trim().length >= 2 ? (
            <div className="disclosure-product-results" aria-live="polite">
              {matchingSolutions.map((solution) => (
                <button key={`solution-${solution.id}`} type="button" onClick={() => setData({
                  productChoice: 'known', solutionId: solution.id, solutionName: solution.name, productId: solution.productId ?? '', privateProductId: solution.privateProductId ?? '', offeringId: solution.offeringId ?? '', productDisplayName: solution.productName ?? solution.name, productSupplierName: solution.supplierName ?? '',
                })}>
                  <span><strong>{solution.productName ?? solution.name}</strong><small>{solution.supplierName ? `${solution.supplierName} · ` : ''}Existing organisational solution: {solution.name}</small></span><Icon name="arrow-right" size={16} />
                </button>
              ))}
              {results.map((result: ProductLibraryResult) => (
                <button key={`product-${result.productId ?? result.templateId}`} type="button" onClick={() => setData({
                  productChoice: 'known', productId: result.productId ?? '', offeringId: result.offeringId ?? '', privateProductId: '', solutionId: '', solutionName: '', productDisplayName: result.productName ?? result.offeringName ?? result.templateName, productSupplierName: result.supplierName ?? '', productTemplateReferenceId: result.templateId,
                })}>
                  <span><strong>{result.productName ?? result.offeringName ?? result.templateName}</strong><small>{[result.supplierName, result.offeringName].filter(Boolean).join(' · ') || 'AI Product Library'}</small></span><Icon name="arrow-right" size={16} />
                </button>
              ))}
              {!resultsQuery.isLoading && matchingSolutions.length === 0 && results.length === 0 ? <p>No matching known product. You can still continue below.</p> : null}
            </div>
          ) : null}
        </>
      )}

      <div className="disclosure-product-alternatives" aria-label="Other product choices">
        <button type="button" className={productChoice === 'another' ? 'selected' : ''} onClick={() => { clearProduct(); setData({ productChoice: 'another' }) }}><Icon name="plus" size={18} /><span><strong>I can't find the product</strong><small>Record the product name you know.</small></span></button>
        <button type="button" className={productChoice === 'internal' ? 'selected' : ''} onClick={() => { clearProduct(); setData({ productChoice: 'internal', privateSupplierName: '' }) }}><Icon name="workflow" size={18} /><span><strong>Built internally</strong><small>An internal assistant, model or AI-enabled tool.</small></span></button>
        <button type="button" className={productChoice === 'unknown' ? 'selected' : ''} onClick={() => { clearProduct(); setData({ productChoice: 'unknown' }) }}><Icon name="help" size={18} /><span><strong>I'm not sure</strong><small>The product can be confirmed later.</small></span></button>
      </div>

      {(productChoice === 'another' || productChoice === 'internal') && !asString(data.privateProductId) ? (
        <div className="disclosure-private-product progressive-reveal">
          {productChoice === 'another' ? <label><span>Supplier <small>Optional</small></span><input value={asString(data.privateSupplierName)} onChange={(event) => setData({ privateSupplierName: event.target.value })} placeholder="e.g. OpenAI" /></label> : null}
          <label><span>{productChoice === 'internal' ? 'What is the internal tool called?' : 'What is the product called?'}</span><input value={asString(data.privateProductName)} onChange={(event) => setData({ privateProductName: event.target.value })} placeholder={productChoice === 'internal' ? 'e.g. Finance assistant' : 'e.g. ChatGPT, Microsoft Copilot, an internal assistant'} /></label>
          <button className="button secondary" type="button" disabled={!asString(data.privateProductName).trim() || creatingPrivateProduct} onClick={() => onCreatePrivateProduct({ supplierName: productChoice === 'internal' ? '' : asString(data.privateSupplierName), productName: asString(data.privateProductName), description: productChoice === 'internal' ? 'Contributor identified this as an internally built AI tool. Technical details require confirmation.' : '', mode: productChoice })}>{creatingPrivateProduct ? 'Recording product…' : 'Use this product'}</button>
          <small>This creates an unconfirmed product reference for your organisation. It does not create a fully assured shared Product record.</small>
        </div>
      ) : null}

      {(productChoice === 'another' || productChoice === 'internal') && asString(data.privateProductId) ? <div className="inline-alert neutral"><strong>{asString(data.privateProductName) || 'Product recorded'}.</strong> The product reference is private to your organisation and can be confirmed later.</div> : null}
      {productChoice === 'unknown' ? <div className="disclosure-soft-note"><Icon name="info" size={17} /><span>Product confirmation can happen later. You can still add the AI use.</span></div> : null}
    </fieldset>
  )
}

function StepOne({
  organisationId,
  draft,
  data,
  setData,
  options,
  solutions,
  onCreatePrivateProduct,
  creatingPrivateProduct,
}: {
  organisationId: string
  draft: AiUseDraft
  data: DraftData
  setData: (patch: DraftData) => void
  options: EntryOptions
  solutions: OrganisationSolution[]
  onCreatePrivateProduct: (input: { supplierName: string; productName: string; description: string; mode: 'another' | 'internal' }) => void
  creatingPrivateProduct: boolean
}) {
  const lifecycle = asString(data.contributorLifecycle) || contributorStateFromOperational(asString(data.operationalState) || 'proposed')
  const accountableBusinessUnitId = asString(data.accountableBusinessUnitId) || asString(data.businessUnitId)
  const usageState = normaliseUsageScopeState(data, options.businessUnits.map((unit) => unit.id))
  const usageScope = usageState.scope
  const usageBusinessUnitIds = usageState.businessUnitIds
  const usageComplete = usageScopeIsComplete(usageState)
  const toggleUsageBusinessUnit = (businessUnitId: string) => {
    const next = usageBusinessUnitIds.includes(businessUnitId)
      ? usageBusinessUnitIds.filter((id) => id !== businessUnitId)
      : [...usageBusinessUnitIds, businessUnitId]
    setData({ usageScope: 'selected_business_units', usageBusinessUnitIds: next })
  }
  return (
    <div className="form-stack disclosure-step-content">
      <div className="disclosure-step-intro"><p>Start with the business activity. You do not need to know how the AI technology works.</p></div>

      <label><span>What should we call this use?</span><input value={asString(data.title)} onChange={(event) => setData({ title: event.target.value })} placeholder="e.g. Using ChatGPT to draft supplier emails" /><small>Use a short name that will make sense to someone else in your organisation.</small></label>

      <label><span>What do you use it to do?</span><textarea rows={4} value={asString(data.businessPurpose)} onChange={(event) => setData({ businessPurpose: event.target.value })} placeholder="Describe what you use the AI for in your own words." /><small>For example: “I use it to summarise invoices and help draft emails to suppliers.” You do not need to explain how the AI works.</small></label>

      <label><span>Which part of the organisation is responsible for this AI use?</span><select value={accountableBusinessUnitId} onChange={(event) => setData({ businessUnitId: event.target.value, accountableBusinessUnitId: event.target.value })}><option value="">I'm not sure</option>{options.businessUnits.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}</select><small>This is the accountable business area. It may be different from every area where the AI is used.</small></label>

      <fieldset className="register-choice-group disclosure-usage-scope" aria-describedby={!usageComplete ? 'usage-scope-error' : undefined}>
        <legend>Where is this AI use currently used?</legend>
        <p className="field-help">Choose every business area that uses it, or record that it is organisation-wide. This does not change the accountable area above.</p>
        <div className="disclosure-card-grid compact">
          <IconChoice type="radio" name="usage-scope" value="selected_business_units" checked={usageScope === 'selected_business_units'} icon="register" title="Specific business areas" description="Choose one or more areas below." onChange={() => setData({ usageScope: 'selected_business_units', usageBusinessUnitIds })} />
          <IconChoice type="radio" name="usage-scope" value="organisation_wide" checked={usageScope === 'organisation_wide'} icon="overview" title="Organisation-wide" description="It is used across the organisation." onChange={() => setData({ usageScope: 'organisation_wide', usageBusinessUnitIds: [] })} />
          <IconChoice type="radio" name="usage-scope" value="unknown" checked={usageScope === 'unknown'} icon="help" title="I'm not sure" description="The usage scope can be confirmed later." onChange={() => setData({ usageScope: 'unknown', usageBusinessUnitIds: [] })} />
        </div>
        {usageScope === 'selected_business_units' ? <div className="disclosure-usage-business-units">{options.businessUnits.map((unit) => <label key={unit.id} className={usageBusinessUnitIds.includes(unit.id) ? 'selected' : ''}><input type="checkbox" checked={usageBusinessUnitIds.includes(unit.id)} onChange={() => toggleUsageBusinessUnit(unit.id)} /><span>{unit.name}</span></label>)}</div> : null}
        {!usageComplete ? <div id="usage-scope-error" className="disclosure-validation-note">Choose at least one business area, or choose Organisation-wide or I'm not sure.</div> : null}
      </fieldset>

      <fieldset className="register-choice-group">
        <legend>Is it currently being used?</legend>
        <div className="disclosure-card-grid compact">
          {lifecycleChoices.map((choice) => <IconChoice key={choice.value} type="radio" name="contributor-lifecycle" value={choice.value} checked={lifecycle === choice.value} title={choice.title} description={choice.description} onChange={() => setData({ contributorLifecycle: choice.value })} />)}
        </div>
      </fieldset>

      <ProductPicker organisationId={organisationId} draft={draft} data={data} setData={setData} solutions={solutions} onCreatePrivateProduct={onCreatePrivateProduct} creatingPrivateProduct={creatingPrivateProduct} />
      <TechnologyFamilyField organisationId={organisationId} draft={draft} data={data} options={options} setData={setData} />
    </div>
  )
}

function StepTwo({ observations, setObservations }: { observations: ContributorObservations; setObservations: (patch: Partial<ContributorObservations>) => void }) {
  const askAction = shouldAskActionAuthority(observations)
  const askAffected = shouldAskAffectedPeople(observations)
  const userGroups = observations.userGroups
  const affectedPeople = observations.affectedPeople ?? []

  return (
    <div className="form-stack disclosure-step-content">
      <div className="disclosure-step-intro"><p>Tell us what happens in this particular use. Choose descriptions you can observe in your work.</p></div>

      <fieldset className="register-choice-group">
        <legend>What does it help you do?</legend>
        <p className="field-help">Select everything that applies to this particular use.</p>
        <div className="disclosure-card-grid">
          {activityChoices.map((choice) => <IconChoice key={choice.value} type="checkbox" name="activity" value={choice.value} checked={observations.activities.includes(choice.value)} icon={choice.icon} title={choice.title} description={choice.description} onChange={() => setObservations({ activities: toggle(observations.activities, choice.value, ['other_unsure']) as ActivityObservation[] })} />)}
        </div>
      </fieldset>

      {observations.activities.includes('other_unsure') ? <label className="progressive-reveal"><span>Tell us what it does <small>Optional</small></span><textarea rows={2} value={observations.otherActivityText ?? ''} onChange={(event) => setObservations({ otherActivityText: event.target.value })} /></label> : null}

      {observations.activities.includes('analyse_predict_recommend') ? (
        <fieldset className="register-choice-group progressive-reveal">
          <legend>Which description best matches the analysis or decision support?</legend>
          <p className="field-help">Choose the closest description. This lets Swell store the correct governance classification without asking you to interpret technical terminology.</p>
          <div className="disclosure-card-grid compact">
            {analysisPurposeChoices.map((choice) => <IconChoice key={choice.value} type="radio" name="analysis-purpose" value={choice.value} checked={observations.analysisPurpose === choice.value} title={choice.title} description={choice.description} onChange={() => setObservations({ analysisPurpose: choice.value })} />)}
          </div>
        </fieldset>
      ) : null}

      {observations.activities.includes('media_understanding') ? (
        <fieldset className="register-choice-group progressive-reveal"><legend>What kind of media does it understand?</legend><div className="disclosure-card-grid compact">{([
          ['images_video', 'Images or video'], ['speech_audio', 'Speech or audio'], ['both', 'Both'], ['unsure', "I'm not sure"],
        ] as const).map(([value, title]) => <IconChoice key={value} type="radio" name="media-type" value={value} checked={observations.mediaType === value} title={title} onChange={() => setObservations({ mediaType: value })} />)}</div></fieldset>
      ) : null}

      {askAction ? <TriQuestion name="action-without-approval" icon="workflow" title="Can it do something in another system without someone approving each action?" help="For example, creating a record, sending an email, updating a system, progressing a transaction or triggering another process." value={observations.actionWithoutApproval} onChange={(value) => setObservations({ actionWithoutApproval: value })} /> : null}

      <fieldset className="register-choice-group disclosure-section-card">
        <legend><span className="disclosure-question-icon"><Icon name="user-check" size={20} /></span>What normally happens before its output is used?</legend>
        <div className="disclosure-card-grid compact">{humanCheckChoices.map((choice) => <IconChoice key={choice.value} type="radio" name="human-check" value={choice.value} checked={observations.humanCheck === choice.value} title={choice.title} description={choice.description} onChange={() => setObservations({ humanCheck: choice.value })} />)}</div>
      </fieldset>

      <fieldset className="register-choice-group disclosure-section-card">
        <legend><span className="disclosure-question-icon"><Icon name="users" size={20} /></span>Who uses it?</legend>
        <p className="field-help">Select everyone who directly uses this AI use.</p>
        <div className="disclosure-card-grid small">{userChoices.map((choice) => <IconChoice key={choice.value} type="checkbox" name="user-group" value={choice.value} checked={userGroups.includes(choice.value)} icon={choice.icon} title={choice.title} onChange={() => setObservations({ userGroups: toggle(userGroups, choice.value, ['needs_confirmation']) })} />)}</div>
      </fieldset>

      {askAffected ? (
        <fieldset className="register-choice-group disclosure-section-card progressive-reveal">
          <legend>Could the output affect any of these people?</legend>
          <p className="field-help">This includes people who may be influenced by a recommendation, decision, communication or action — even if they do not use the AI themselves.</p>
          <div className="disclosure-card-grid small">{affectedChoices.map((choice) => <IconChoice key={choice.value} type="checkbox" name="affected-people" value={choice.value} checked={affectedPeople.includes(choice.value)} title={choice.title} onChange={() => setObservations({ affectedPeople: toggle(affectedPeople, choice.value, ['no_identifiable_people', 'needs_confirmation']) })} />)}</div>
        </fieldset>
      ) : null}
    </div>
  )
}

function StepThree({ data, setData }: { data: DraftData; setData: (patch: DraftData) => void }) {
  const benefits = asStrings(data.expectedBenefits)
  return (
    <div className="form-stack disclosure-step-content">
      <div className="disclosure-step-intro"><p>Tell us what improvement you expect from this use. You do not need to connect it to the organisation's strategy or roadmap yourself.</p></div>
      <fieldset className="register-choice-group"><legend>What improvement are you expecting?</legend><p className="field-help">Select everything that applies.</p><div className="disclosure-card-grid">{benefitChoices.map((choice) => <IconChoice key={choice.value} type="checkbox" name="expected-benefit" value={choice.value} checked={benefits.includes(choice.value)} icon={choice.icon} title={choice.title} description={choice.description} onChange={() => setData({ expectedBenefits: toggle(benefits, choice.value) })} />)}</div></fieldset>
      <label><span>Tell us a little more about the expected improvement <small>Optional</small></span><textarea rows={3} value={asString(data.benefitExplanation)} onChange={(event) => setData({ benefitExplanation: event.target.value })} placeholder="e.g. Reduce the time spent manually entering invoice information." /></label>
      <label><span>How would you know this was helping? <small>Optional</small></span><input value={asString(data.intendedSuccessMeasure)} onChange={(event) => setData({ intendedSuccessMeasure: event.target.value })} placeholder="e.g. We spend less time manually entering invoice information." /></label>
      <div className="disclosure-soft-note"><Icon name="info" size={17} /><span>Strategy and roadmap alignment can be completed later. Unplanned or shadow AI still belongs in the AI Register.</span></div>
    </div>
  )
}

function EarlierAnswer({ title, answer, onEdit }: { title: string; answer: string; onEdit: () => void }) {
  return <div className="disclosure-earlier-answer"><span><small>You told us earlier</small><strong>{title}</strong><span>{answer}</span></span><button type="button" className="text-link" onClick={onEdit}>Edit</button></div>
}

function StepFour({ observations, setObservations, preview, onEditEarlier }: {
  observations: ContributorObservations
  setObservations: (patch: Partial<ContributorObservations>) => void
  preview: ScreeningPreview | undefined
  onEditEarlier: () => void
}) {
  const firstPairComplete = Boolean(observations.informationHandling && observations.importantDecision)
  const secondPairComplete = Boolean(observations.seriousImpact && observations.regulatedArea)
  const askVulnerable = shouldAskVulnerablePeople(observations)
  const askContestability = shouldAskContestability(observations)
  const actionSummary = !shouldAskActionAuthority(observations)
    ? 'This use was not described as carrying out process actions.'
    : observations.actionWithoutApproval === 'yes'
      ? 'It may take actions without someone approving each one.'
      : observations.actionWithoutApproval === 'no'
        ? 'Each system action requires approval first.'
        : 'Whether system actions need approval still needs confirmation.'

  return (
    <div className="form-stack disclosure-step-content">
      <div className="disclosure-step-intro"><p>These questions help Swell work out whether another person may need to review this use. They are not a risk rating or approval.</p></div>
      <div className="disclosure-reassurance"><Icon name="help" size={19} /><span><strong>Not sure? That's okay.</strong> Choose “I'm not sure” and Swell can route the question to the right person.</span></div>

      <div className="disclosure-question-grid">
        <TriQuestion name="information-handling" icon="lock" title="Does this AI use personal, sensitive or confidential information?" help="Think about employee, customer, supplier, financial, health, commercial or other information that should not be publicly available." value={observations.informationHandling} onChange={(value) => setObservations({ informationHandling: value })} />
        <TriQuestion name="important-decision" icon="scale" title="Can its output influence an important decision about someone?" help="For example, whether someone receives a job, payment, service, benefit, approval, opportunity or another significant outcome." value={observations.importantDecision} onChange={(value) => setObservations({ importantDecision: value })} />
      </div>

      {firstPairComplete ? (
        <div className="disclosure-question-grid progressive-reveal">
          <TriQuestion name="serious-impact" icon="alert" title="Could a mistake have a serious impact?" help="Think about financial loss, safety, someone being treated unfairly, disruption to an important service, or a decision that would be difficult to correct." value={observations.seriousImpact} onChange={(value) => setObservations({ seriousImpact: value })} />
          <TriQuestion name="regulated-area" icon="scale" title="Is it used in an area with special legal, regulatory or professional requirements?" help="You do not need to know the law. Think about areas such as employment, health, safety, finance, privacy, education, essential services or regulated professional work." value={observations.regulatedArea} onChange={(value) => setObservations({ regulatedArea: value })} />
        </div>
      ) : null}

      <EarlierAnswer title="Can it take actions without individual approval?" answer={actionSummary} onEdit={onEditEarlier} />

      {firstPairComplete && secondPairComplete && (askVulnerable || askContestability) ? (
        <div className="disclosure-question-grid progressive-reveal">
          {askVulnerable ? <TriQuestion name="vulnerable-people" icon="heart" title="Could it affect people who may need additional protection or support?" help="For example, children, people receiving care, people with disability, people experiencing vulnerability, or groups that may be unfairly disadvantaged." value={observations.vulnerablePeople} onChange={(value) => setObservations({ vulnerablePeople: value })} /> : null}
          {askContestability ? <TriQuestion name="contestability" icon="scale" title="If the AI got something wrong, would it be difficult to correct or challenge the outcome?" help="Consider whether someone could identify the problem, have it reviewed and correct any resulting decision or action." value={observations.contestability} onChange={(value) => setObservations({ contestability: value })} /> : null}
        </div>
      ) : null}

      {preview ? (
        <section className={`disclosure-screening-summary ${preview.outcomeCode}`} aria-live="polite">
          <span className="disclosure-choice-icon"><Icon name="shield" size={20} /></span>
          <div><small>Initial governance check</small><strong>{preview.outcomeCode === 'no_immediate_attention_signal' ? 'Completed' : 'Completed — areas may need review'}</strong><p>Swell has applied a preliminary deterministic check. You will be able to review the recorded facts behind any follow-up after saving.</p></div>
        </section>
      ) : null}
    </div>
  )
}

function findingPresentation(finding: ScreeningPreview['findings'][number]) {
  const titles: Record<string, string> = {
    'GAS-01': 'Privacy or information-handling review may be required',
    'GAS-02': 'Interaction and oversight review may be required',
    'GAS-03': 'Action authority needs specialist confirmation',
    'GAS-04': 'People-impact review may be required',
    'GAS-05': 'Legal or regulatory review may be required',
    'GAS-06': 'Correction and contestability review may be required',
    'GAS-07': 'Product characteristics need confirmation',
  }
  return titles[finding.questionCode] ?? finding.title
}

function StepFive({ draft, data, observations, options, preview, isEditMode }: { draft: AiUseDraft; data: DraftData; observations: ContributorObservations; options: EntryOptions; preview: ScreeningPreview | undefined; isEditMode: boolean }) {
  const classifications = asObject(data.classifications)
  const primaryActivity = asString(classifications.primary_ai_activity)
  const primaryActivityLabel = options.classificationSets.ai_activity?.find((item) => item.valueCode === primaryActivity)?.label ?? "Needs confirmation"
  const accountableBusinessUnitId = asString(data.accountableBusinessUnitId) || asString(data.businessUnitId)
  const accountableBusinessUnit = options.businessUnits.find((unit) => unit.id === accountableBusinessUnitId)?.name ?? "I'm not sure"
  const usageState = normaliseUsageScopeState(data, options.businessUnits.map((unit) => unit.id))
  const usageScope = usageState.scope
  const usageBusinessUnitIds = usageState.businessUnitIds
  const usageBusinessUnits = options.businessUnits.filter((unit) => usageBusinessUnitIds.includes(unit.id)).map((unit) => unit.name)
  const usageScopeLabel = usageScope === 'organisation_wide'
    ? 'Organisation-wide'
    : usageScope === 'selected_business_units'
      ? (usageBusinessUnits.length ? usageBusinessUnits.join(', ') : 'Needs confirmation')
      : "I'm not sure"
  const product = asString(data.productDisplayName) || draft.catalogueSnapshot?.products[0]?.name || (asString(data.privateProductName) || "I'm not sure")
  const lifecycle = lifecycleLabels[asString(data.contributorLifecycle)] ?? lifecycleLabels[contributorStateFromOperational(asString(data.operationalState) || 'proposed')]
  const benefits = asStrings(data.expectedBenefits)
  const confirmations = [
    !asString(data.productId) && !asString(data.privateProductId) && !asString(data.solutionId) ? 'Which approved AI product is being used' : null,
    asStrings(classifications.technology_family).includes('needs_technical_confirmation') ? 'Technical classification' : null,
    !asString(data.technicalOwnerMembershipId) ? 'Technical owner' : null,
    !asString(data.businessOwnerMembershipId) ? 'Accountable business owner' : null,
    !accountableBusinessUnitId ? 'Accountable business area' : null,
    preview?.findings.some((finding) => finding.questionCode === 'GAS-07') ? 'Product characteristics needed for the governance check' : null,
    asStrings(classifications.ai_activity).includes('other_needs_confirmation') || asString(classifications.operating_pattern) === 'other_needs_confirmation' ? 'How the AI activity or operating pattern is classified' : null,
    !asString(data.primaryObjectiveId) ? 'Strategic alignment' : null,
  ].filter((item, index, all): item is string => Boolean(item) && all.indexOf(item) === index)

  return (
    <div className="form-stack disclosure-step-content">
      <div className="disclosure-step-intro"><h3>{isEditMode ? "You're ready to save these changes" : "You're ready to add this use"}</h3><p>{isEditMode ? 'Check the updated information. Swell will keep the earlier history and recalculate only the deterministic results affected by these changes.' : 'Check what you have told us. You can still add the use even when some information needs to be confirmed by someone else.'}</p></div>

      <section className="register-review-card disclosure-review-primary">
        <p className="eyebrow">What you told us</p>
        <h2>{asString(data.title) || 'Untitled AI use'}</h2>
        <p>{asString(data.businessPurpose) || 'Business purpose still needs to be described.'}</p>
        <dl className="register-definition-list two-column">
          <div><dt>Stage</dt><dd>{lifecycle}</dd></div>
          <div><dt>Accountable business area</dt><dd>{accountableBusinessUnit}</dd></div>
          <div><dt>Usage scope</dt><dd>{usageScopeLabel}</dd></div>
          <div><dt>Used by</dt><dd>{observations.userGroups.length ? observations.userGroups.map((code) => userLabels[code] ?? code).join(', ') : "I'm not sure"}</dd></div>
          <div><dt>AI product</dt><dd>{product}</dd></div>
          <div><dt>Swell classification</dt><dd>{primaryActivityLabel}<small>Derived from the plain-English activity answers.</small></dd></div>
          <div><dt>Human check</dt><dd>{observations.humanCheck ? humanCheckLabels[observations.humanCheck] : "I'm not sure"}</dd></div>
          <div><dt>Initial governance check</dt><dd>{preview ? (preview.outcomeCode === 'no_immediate_attention_signal' ? 'Completed' : 'Completed — additional review required') : 'Needs completion'}</dd></div>
          <div><dt>Formal assessment</dt><dd>{isEditMode ? 'Current assessment position retained until changes are saved and evaluated' : 'Ready to start after the AI use is saved'}</dd></div>
        </dl>
      </section>

      {benefits.length > 0 || asString(data.benefitExplanation) ? <section className="register-review-card"><p className="eyebrow">Why it is being used</p>{benefits.length > 0 ? <><h3>Expected improvement</h3><div className="register-chip-list">{benefits.map((code) => <span key={code}>{benefitLabels[code] ?? code}</span>)}</div></> : null}{asString(data.benefitExplanation) ? <p>{asString(data.benefitExplanation)}</p> : null}{asString(data.intendedSuccessMeasure) ? <p><strong>How we would know:</strong> {asString(data.intendedSuccessMeasure)}</p> : null}</section> : null}

      {preview?.findings.length ? <section className="register-review-card disclosure-identified"><p className="eyebrow">Initial triage</p><h2>Areas that may need review</h2><p>These are preliminary signals based on the information recorded so far. They are not formal assignments or review requests.</p>{preview.findings.map((finding) => {
        const productFinding = finding.questionCode === 'GAS-07'
        return <article key={finding.ruleCode} className="disclosure-finding"><span className="disclosure-choice-icon"><Icon name={finding.questionCode === 'GAS-01' ? 'lock' : 'shield'} size={19} /></span><div><h3>{productFinding ? 'Product can be adapted to many different uses' : findingPresentation(finding)}</h3><p>{productFinding ? 'The product may be general-purpose or adaptable beyond the intended use. Swell needs to confirm its intended-use boundaries and review triggers in the formal assessment.' : finding.implication}</p><small><strong>Why this appeared:</strong> {productFinding ? 'The recognised product characteristic has not yet been confirmed.' : `The recorded answer for ${finding.question} was ${finding.answer === 'yes' ? 'Yes' : 'Needs confirmation'}, which meets the preliminary condition for this area.`}</small><small><strong>Likely follow-up:</strong> Continue to the formal assessment to determine whether specialist review is needed.</small></div></article>
      })}</section> : null}

      {confirmations.length > 0 ? <section className="register-review-card disclosure-confirmations"><p className="eyebrow">Someone else needs to confirm</p><ul>{confirmations.map((item) => <li key={item}><Icon name="help" size={17} /><span>{item}</span></li>)}</ul><p><strong>You do not need to find these answers yourself.</strong> Swell can route them to the appropriate person.</p></section> : null}

      <section className="register-review-card disclosure-next"><span className="disclosure-choice-icon"><Icon name="info" size={20} /></span><div><h3>What happens next</h3><p>{isEditMode ? <>Saving these changes updates the same AI Register record and preserves its amendment history. Swell will recalculate affected deterministic results. If the use was already approved, governance-relevant changes may require reassessment or reapproval through the existing Material Change process.</> : <>Adding this use records it in the AI Register. It does <strong>not</strong> approve the use. If an assessment, control, specialist review or approval is required, Swell will track that separately.</>}</p><p className="disclosure-lifecycle-copy">Register → Assess → Control → Approve → Monitor → Review</p></div></section>
    </div>
  )
}

export function AiUseWizardPage() {
  const { draftId = '' } = useParams()
  const { current } = useTenant()
  const organisationId = current?.organisation.id ?? ''
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [step, setStep] = useState(1)
  const [data, setDataState] = useState<DraftData>({})
  const [observations, setObservationsState] = useState<ContributorObservations>({ activities: [], userGroups: [] })
  const [confirmedFields, setConfirmedFields] = useState<string[]>([])
  const [hydratedDraftId, setHydratedDraftId] = useState('')
  const [completedUseCaseId, setCompletedUseCaseId] = useState('')
  const [draftSaveState, setDraftSaveState] = useState<DraftSaveState>({ phase: 'idle' })
  const [finaliseIssue, setFinaliseIssue] = useState<FinaliseIssue | null>(null)
  const [finaliseStarting, setFinaliseStarting] = useState(false)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const lastSavedRef = useRef('')
  const currentFingerprintRef = useRef('')
  const finaliseInFlightRef = useRef(false)
  const autosaveTimerRef = useRef<number | null>(null)
  const saveRevisionRef = useRef(0)

  const draftQuery = useQuery({ queryKey: ['ai-use-draft', draftId], queryFn: () => getAiUseDraft(draftId), enabled: Boolean(draftId) })
  const optionsQuery = useQuery({ queryKey: ['ai-use-entry-options', organisationId], queryFn: () => getAiUseEntryOptions(organisationId), enabled: Boolean(organisationId) })
  const solutionsQuery = useQuery({ queryKey: ['organisation-ai-solutions', organisationId], queryFn: () => listOrganisationAiSolutions(organisationId), enabled: Boolean(organisationId) })

  const setData = (patch: DraftData) => setDataState((currentData) => ({ ...currentData, ...patch }))
  const setObservations = (patch: Partial<ContributorObservations>) => setObservationsState((currentObservations) => ({ ...currentObservations, ...patch }))

  useEffect(() => {
    const draft = draftQuery.data
    if (!draft || hydratedDraftId === draft.id) return
    const suggestedClassifications = asObject(asObject(draft.suggestedFields).classifications)
    const storedClassifications = asObject(draft.draftData.classifications)
    const hydratedData: DraftData = {
      ...draft.draftData,
      classifications: { ...suggestedClassifications, ...storedClassifications },
    }
    if (asStrings(suggestedClassifications.technology_family).length > 0 && asStrings(storedClassifications.technology_family).length === 0) {
      hydratedData.technologyFamilySource = 'product_library'
    }
    if (!asString(hydratedData.contributorLifecycle)) hydratedData.contributorLifecycle = contributorStateFromOperational(asString(hydratedData.operationalState) || 'proposed')
    if (!asString(hydratedData.productId) && !asString(hydratedData.privateProductId) && !asString(hydratedData.solutionId) && draft.catalogueSnapshot?.products[0]) {
      hydratedData.productChoice = 'known'
      hydratedData.productId = draft.catalogueSnapshot.products[0].id
      hydratedData.offeringId = draft.catalogueSnapshot.offerings[0]?.id ?? ''
      hydratedData.productDisplayName = draft.catalogueSnapshot.products[0].name
      hydratedData.productSupplierName = draft.catalogueSnapshot.products[0].supplierName ?? ''
    }
    setDataState(hydratedData)
    setObservationsState(legacyObservations(hydratedData))
    setConfirmedFields(draft.confirmedFields)
    const nextStep = Math.min(5, Math.max(1, draft.currentStep || 1))
    setStep(nextStep)
    const fingerprint = JSON.stringify({ data: prepareDraftData(draft, hydratedData, legacyObservations(hydratedData)), confirmedFields: draft.confirmedFields, step: nextStep })
    lastSavedRef.current = fingerprint
    setDraftSaveState({ phase: 'saved', fingerprint, savedAt: draft.updatedAt })
    setHydratedDraftId(draft.id)
  }, [draftQuery.data, hydratedDraftId])

  const draft = draftQuery.data
  const isEditMode = Boolean(asString(data.editUseCaseId) || asString(draft?.draftData.editUseCaseId))
  const activeBusinessUnitIds = useMemo(() => optionsQuery.data?.businessUnits.map((unit) => unit.id), [optionsQuery.data])
  const preparedData = useMemo(() => draft ? prepareDraftData(draft, data, observations, activeBusinessUnitIds) : data, [activeBusinessUnitIds, data, draft, observations])
  const currentFingerprint = useMemo(() => JSON.stringify({ data: preparedData, confirmedFields, step }), [confirmedFields, preparedData, step])
  currentFingerprintRef.current = currentFingerprint

  const saveQueue = useMemo(() => createSerialSaveQueue<DraftSaveInput>(
    (input) => saveAiUseDraft(draftId, input.nextStep, input.data, input.fields),
    (input) => input.revision === saveRevisionRef.current,
  ), [draftId])
  const saveDraft = useCallback(async (input: DraftSaveInput) => {
    if (input.revision !== saveRevisionRef.current) return
    const fingerprint = JSON.stringify({ data: input.data, confirmedFields: input.fields, step: input.nextStep })
    const request = saveQueue.enqueue(input)
    setDraftSaveState({ phase: 'saving', fingerprint })

    try {
      const outcome = await request.promise
      if (outcome === 'skipped') return
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ai-use-draft', draftId] }),
        queryClient.invalidateQueries({ queryKey: ['ai-use-drafts', organisationId] }),
      ])
      if (request.isLatest()) {
        lastSavedRef.current = fingerprint
        if (currentFingerprintRef.current === fingerprint) {
          setDraftSaveState({ phase: 'saved', fingerprint, savedAt: new Date().toISOString() })
        } else {
          setDraftSaveState({ phase: 'dirty', fingerprint: currentFingerprintRef.current })
        }
      }
    } catch (error) {
      if (request.isLatest()) setDraftSaveState({ phase: 'failed', fingerprint, error })
      throw error
    }
  }, [draftId, organisationId, queryClient, saveQueue])

  const cancelPendingAutosave = useCallback(() => {
    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
    }
    saveRevisionRef.current += 1
  }, [])

  const saveExplicitly = useCallback(async (input: Omit<DraftSaveInput, 'revision'>) => {
    cancelPendingAutosave()
    await saveDraft({ ...input, revision: saveRevisionRef.current })
  }, [cancelPendingAutosave, saveDraft])

  useEffect(() => {
    if (!hydratedDraftId || hydratedDraftId !== draftId) return
    if (currentFingerprint === lastSavedRef.current) {
      setDraftSaveState((state) => state.phase === 'failed' || state.phase === 'saving'
        ? state
        : { phase: 'saved', fingerprint: currentFingerprint, savedAt: state.savedAt })
      return
    }
    setDraftSaveState((state) => state.phase === 'failed' || state.phase === 'saving'
      ? state
      : { phase: 'dirty', fingerprint: currentFingerprint })
  }, [currentFingerprint, draftId, hydratedDraftId])

  const privateProductMutation = useMutation({
    mutationFn: async (input: { supplierName: string; productName: string; description: string; mode: 'another' | 'internal' }) => ({ ...input, id: await createPrivateProductForDraft(draftId, input.supplierName, input.productName, input.description) }),
    onSuccess: (result) => setData({ privateProductId: result.id, productChoice: result.mode, productDisplayName: result.productName, productSupplierName: result.supplierName }),
  })

  const screeningReady = useMemo(() => {
    const basics = Boolean(observations.informationHandling && observations.importantDecision && observations.seriousImpact && observations.regulatedArea)
    if (!basics) return false
    if (shouldAskVulnerablePeople(observations) && !observations.vulnerablePeople) return false
    if (shouldAskContestability(observations) && !observations.contestability) return false
    return true
  }, [observations])

  const screeningAnswers = useMemo(() => Object.fromEntries(Object.entries(asObject(preparedData.screeningAnswers)).filter((entry): entry is [string, string] => typeof entry[1] === 'string')), [preparedData.screeningAnswers])
  const previewQuery = useQuery({ queryKey: ['ai-use-screening-preview', organisationId, screeningAnswers], queryFn: () => previewAiUseScreening(organisationId, screeningAnswers), enabled: Boolean(organisationId && screeningReady) })
  const usageStateForRevision = useMemo(() => normaliseUsageScopeState(data, activeBusinessUnitIds), [activeBusinessUnitIds, data])
  const usageRevision = useMemo(() => JSON.stringify({ scope: usageStateForRevision.scope, businessUnitIds: usageStateForRevision.businessUnitIds }), [usageStateForRevision.businessUnitIds, usageStateForRevision.scope])
  const usageScopeCompleteForRevision = usageScopeIsComplete(usageStateForRevision)

  useEffect(() => {
    setFinaliseIssue((issue) => issue
      && issue.kind === 'usage_validation'
      && issue.usageRevision !== usageRevision
      && usageScopeCompleteForRevision
      ? null
      : issue)
  }, [usageRevision, usageScopeCompleteForRevision])

  const finaliseMutation = useMutation({
    mutationFn: async (input: { continueToAssessment: boolean; usageRevision: string }) => ({ useCaseId: await finaliseAiUseDraft(draftId), continueToAssessment: input.continueToAssessment }),
    onMutate: () => setFinaliseIssue(null),
    onError: (error, input) => setFinaliseIssue({
      error,
      kind: isUsageScopeValidationError(error) ? 'usage_validation' : 'operation',
      usageRevision: input.usageRevision,
    }),
    onSuccess: async ({ useCaseId, continueToAssessment }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ai-register-entry', useCaseId] }),
        queryClient.invalidateQueries({ queryKey: ['ai-use-private-product', useCaseId] }),
        queryClient.invalidateQueries({ queryKey: ['phase3-governance-position', useCaseId] }),
        queryClient.invalidateQueries({ queryKey: ['ai-use-record-capabilities', useCaseId] }),
        queryClient.invalidateQueries({ queryKey: ['ai-use-workspace-context', useCaseId] }),
        queryClient.invalidateQueries({ queryKey: ['ai-register'] }),
        queryClient.invalidateQueries({ queryKey: ['p4-use-cases'] }),
        queryClient.invalidateQueries({ queryKey: ['organisation-ai-solutions', organisationId] }),
        queryClient.invalidateQueries({ queryKey: ['ai-use-drafts', organisationId] }),
      ])
      if (continueToAssessment) {
        void navigate(`/assess/${useCaseId}?from=add`)
        return
      }
      setCompletedUseCaseId(useCaseId)
    },
    onSettled: () => { finaliseInFlightRef.current = false },
  })

  const discardMutation = useMutation({
    mutationFn: () => abandonAiUseDraft(draftId),
    onSuccess: async () => {
      cancelPendingAutosave()
      await queryClient.invalidateQueries({ queryKey: ['ai-use-drafts', organisationId] })
      const publishedUseCaseId = asString(draft?.draftData.editUseCaseId)
      void navigate(publishedUseCaseId ? `/ai-register/${publishedUseCaseId}` : '/ai-register')
    },
  })

  useEffect(() => {
    if (!hydratedDraftId || hydratedDraftId !== draftId || !draft || draft.status !== 'draft') return
    if (currentFingerprint === lastSavedRef.current) return
    const revision = saveRevisionRef.current + 1
    saveRevisionRef.current = revision
    const timer = window.setTimeout(() => {
      if (autosaveTimerRef.current === timer) autosaveTimerRef.current = null
      void saveDraft({ nextStep: step, data: preparedData, fields: confirmedFields, revision }).catch(() => undefined)
    }, 850)
    autosaveTimerRef.current = timer
    return () => {
      window.clearTimeout(timer)
      if (autosaveTimerRef.current === timer) autosaveTimerRef.current = null
    }
  }, [confirmedFields, currentFingerprint, draft, draftId, hydratedDraftId, preparedData, saveDraft, step])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (currentFingerprint !== lastSavedRef.current && !completedUseCaseId) { event.preventDefault(); event.returnValue = '' }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [completedUseCaseId, currentFingerprint])

  if (!current) return <Navigate to="/workspace/create" replace />
  if (draftQuery.isLoading || optionsQuery.isLoading || solutionsQuery.isLoading) return <div className="page-stack register-page"><section className="panel"><p>Loading your AI-use draft…</p></section></div>
  const options = optionsQuery.data
  if (!draft || !options) return <div className="page-stack register-page"><PageHeader eyebrow="AI Register" title="Draft not available" description="This AI-use draft could not be loaded or is outside your current access." /><ErrorAlert error={draftQuery.error ?? optionsQuery.error} /></div>
  if (draft.status === 'converted' && draft.convertedUseCaseId && !completedUseCaseId) return <Navigate to={`/ai-register/${draft.convertedUseCaseId}`} replace />

  if (completedUseCaseId) {
    return (
      <div className="page-stack register-page register-wizard">
        <section className="panel disclosure-success" aria-live="polite">
          <span className="disclosure-success-icon"><Icon name="check" size={30} /></span>
          <p className="eyebrow">AI Register</p>
          <h1>{isEditMode ? 'AI use updated' : 'AI use added'}</h1>
          <p>{isEditMode ? 'The same AI Register record has been updated. Earlier information remains in its governance history, and affected deterministic results have been recalculated.' : 'It is now visible in the AI Register. You can continue into the deterministic assessment now or return later from the AI Use record.'}</p>
          <div className="page-header-buttons">
            {!isEditMode && options.canManageRegister ? <button className="button primary" type="button" onClick={() => void navigate(`/assess/${completedUseCaseId}?from=add`)}>Continue to assessment</button> : null}
            <button className={isEditMode || !options.canManageRegister ? 'button primary' : 'button secondary'} type="button" onClick={() => void navigate(`/ai-register/${completedUseCaseId}`)}>{isEditMode ? 'View updated AI use' : 'View AI use'}</button>
            <button className="button secondary" type="button" onClick={() => void navigate('/ai-register')}>Return to AI Register</button>
          </div>
        </section>
      </div>
    )
  }

  const selectedTechnologyFamily = asStrings(asObject(preparedData.classifications).technology_family)
  const technologyFamilyComplete = selectedTechnologyFamily.length === 1
    && selectedTechnologyFamily[0] !== 'needs_technical_confirmation'
  const requiresTechnologySelection = !(
    asString(data.productTemplateReferenceId)
      || draft.templateId
      || draft.catalogueSnapshot?.content
  )
  const usageState = usageStateForRevision
  const usageScopeComplete = usageScopeIsComplete(usageState)
  const stepOneValid = Boolean(
    asString(data.title).trim()
    && asString(data.businessPurpose).trim()
    && usageScopeComplete
    && (!requiresTechnologySelection || technologyFamilyComplete),
  )
  const stepTwoValid = Boolean(
    observations.activities.length > 0
      && observations.humanCheck
      && observations.userGroups.length > 0
      && (!shouldAskActionAuthority(observations) || observations.actionWithoutApproval)
      && (!observations.activities.includes('media_understanding') || observations.mediaType)
      && (!observations.activities.includes('analyse_predict_recommend') || observations.analysisPurpose),
  )
  const stepFourValid = screeningReady
  const canAdvance = step === 1 ? stepOneValid : step === 2 ? stepTwoValid : step === 4 ? stepFourValid : true
  const canFinalise = stepOneValid && stepTwoValid && stepFourValid && technologyFamilyComplete
  const furthestAccessibleStep = !stepOneValid ? 1 : !stepTwoValid ? 2 : !stepFourValid ? 4 : 5
  const isFinalising = finaliseStarting || finaliseMutation.isPending

  const saveNow = async (nextStep = step) => {
    await saveExplicitly({ nextStep, data: preparedData, fields: confirmedFields })
  }

  const goToStep = async (next: number) => {
    if (next > step && !canAdvance) return
    try {
      await saveNow(next)
      setStep(next)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {
      // The explicit draft-save failure remains visible with Retry.
    }
  }

  const saveAndLeave = async () => {
    try {
      await saveNow(step)
      void navigate('/ai-register')
    } catch {
      // Stay on the wizard so the user can retry the failed draft save.
    }
  }

  const retryDraftSave = () => {
    void saveExplicitly({ nextStep: step, data: preparedData, fields: confirmedFields }).catch(() => undefined)
  }

  const startFinalise = async (continueToAssessment: boolean) => {
    if (finaliseInFlightRef.current || !canFinalise) return
    finaliseInFlightRef.current = true
    setFinaliseStarting(true)
    setFinaliseIssue(null)
    const expectedFingerprint = currentFingerprintRef.current
    const expectedUsageRevision = usageRevision
    try {
      await saveNow(5)
      if (currentFingerprintRef.current !== expectedFingerprint) {
        finaliseInFlightRef.current = false
        return
      }
      finaliseMutation.mutate({ continueToAssessment, usageRevision: expectedUsageRevision })
    } catch {
      finaliseInFlightRef.current = false
    } finally {
      setFinaliseStarting(false)
    }
  }

  return (
    <div className="page-stack register-page register-wizard disclosure-wizard">
      <Breadcrumbs items={[{ label: 'AI Uses', to: '/ai-register' }, { label: isEditMode ? 'Edit AI use' : 'Add AI use' }]} />
      <PageHeader eyebrow="AI Register" title={isEditMode ? 'Edit AI use' : 'Add an AI use'} description={isEditMode ? 'This private working draft leaves the published AI Use unchanged until you save changes. Discard it at any time if no update is needed.' : 'Tell us about one way AI is being used in the organisation. You only need to answer what you know — if something needs specialist confirmation, Swell can route it to the right person.'} action={<div className="page-header-buttons"><button className="button secondary" type="button" disabled={isFinalising || discardMutation.isPending} onClick={() => void saveAndLeave()}>Save and leave</button><button className="button text danger-text" type="button" disabled={isFinalising || discardMutation.isPending} onClick={() => setConfirmingDiscard(true)}>Discard draft</button></div>} />

      <nav className="register-stepper disclosure-stepper" aria-label={isEditMode ? 'Edit AI use steps' : 'Add an AI use steps'}>{steps.map((label, index) => { const stepNumber = index + 1; const unavailable = stepNumber > furthestAccessibleStep; return <button key={label} type="button" disabled={unavailable || isFinalising} title={unavailable ? 'Complete the earlier required information first' : undefined} aria-current={step === stepNumber ? 'step' : undefined} className={step === stepNumber ? 'current' : step > stepNumber ? 'complete' : ''} onClick={() => void goToStep(stepNumber)}><span>{stepNumber}</span><strong>{label}</strong></button> })}</nav>
      <div className="register-autosave" aria-live="polite">
        {draftSaveState.phase === 'saving' ? 'Saving draft…' : null}
        {draftSaveState.phase === 'dirty' ? 'Changes not yet saved.' : null}
        {draftSaveState.phase === 'saved' ? `Draft saved${draftSaveState.savedAt ? ` · ${displayDateTime(draftSaveState.savedAt)}` : ''}` : null}
        {draftSaveState.phase === 'failed' ? <><span>Draft could not be saved. Your changes remain on this page.</span> <button type="button" className="text-link" onClick={retryDraftSave}>Retry draft save</button></> : null}
      </div>
      <ErrorAlert error={draftQuery.error ?? optionsQuery.error ?? solutionsQuery.error ?? privateProductMutation.error ?? previewQuery.error ?? discardMutation.error ?? finaliseIssue?.error} />

      <section className="panel register-wizard-panel disclosure-stage" key={step}>
        <div className="section-heading disclosure-stage-heading"><div><p className="eyebrow">Step {step} of 5</p><h2>{steps[step - 1]}</h2></div></div>

        {step === 1 ? <StepOne organisationId={organisationId} draft={draft} data={data} setData={setData} options={options} solutions={solutionsQuery.data ?? []} onCreatePrivateProduct={(input) => privateProductMutation.mutate(input)} creatingPrivateProduct={privateProductMutation.isPending} /> : null}
        {step === 2 ? <StepTwo observations={observations} setObservations={setObservations} /> : null}
        {step === 3 ? <StepThree data={data} setData={setData} /> : null}
        {step === 4 ? <StepFour observations={observations} setObservations={setObservations} preview={previewQuery.data} onEditEarlier={() => setStep(2)} /> : null}
        {step === 5 ? <StepFive draft={draft} data={preparedData} observations={observations} options={options} preview={previewQuery.data} isEditMode={isEditMode} /> : null}

        {step === 1 && !stepOneValid ? <div className="disclosure-validation-note">To continue, give the use a short name, describe what it does, choose a valid usage scope and choose one technology family for a new or internally built AI.</div> : null}
        {step === 2 && !stepTwoValid ? <div className="disclosure-validation-note">Choose at least one description of what it does, complete any follow-up question shown, say how outputs are normally checked and identify who directly uses it. “I'm not sure” is a valid answer where offered.</div> : null}
        {step === 4 && !stepFourValid ? <div className="disclosure-validation-note">Answer the questions currently shown. “I'm not sure” is a valid answer and will not block the disclosure.</div> : null}
        {step === 5 && !technologyFamilyComplete ? <div className="disclosure-validation-note">Before adding this AI use, confirm one Technology Family. Choose a Product Library item, or select the closest family for a new or internally built AI.</div> : null}

        <div className="register-wizard-actions">
          <div>{step > 1 ? <button className="button secondary" type="button" disabled={isFinalising} onClick={() => void goToStep(step - 1)}>Back</button> : null}</div>
          <div className="register-wizard-actions-right">
            <button className="button secondary" type="button" disabled={isFinalising} onClick={() => void saveExplicitly({ nextStep: step, data: preparedData, fields: confirmedFields }).catch(() => undefined)}>Save as draft</button>
            {step < 5 ? <button className="button primary" type="button" disabled={!canAdvance || isFinalising} onClick={() => void goToStep(step + 1)}>Continue</button> : <>
              <button className={isEditMode || !options.canManageRegister ? 'button primary' : 'button secondary'} type="button" disabled={isFinalising || !canFinalise} onClick={() => void startFinalise(false)}>{isFinalising ? 'Saving AI use…' : (isEditMode ? 'Save changes' : 'Save AI use')}</button>
              {!isEditMode && options.canManageRegister ? <button className="button primary" type="button" disabled={isFinalising || !canFinalise} onClick={() => void startFinalise(true)}>{isFinalising ? 'Saving AI use…' : 'Save and continue assessment'}</button> : null}
            </>}
          </div>
        </div>
      </section>
      {confirmingDiscard ? <DiscardDraftDialog name={asString(data.title) || 'Untitled AI use'} pending={discardMutation.isPending} onClose={() => setConfirmingDiscard(false)} onConfirm={() => discardMutation.mutate()} /> : null}
    </div>
  )
}
